/**
 * verificationTransport — the ONLY way verification mutations leave the app.
 *
 * WHY THIS EXISTS
 *
 * The global fetch adapter (lib/utils/fetch-with-timeout.ts) gives every
 * Supabase call a 15-second budget and one automatic retry on transient
 * network errors. That combination is fatal for verification:
 *
 *   - verify-identity legitimately runs OCR + an official registry lookup
 *     with a server-side budget of 40s + 35s. On a slow mobile network the
 *     client aborts at 15s, shows an error, and the user retries — while the
 *     edge function keeps running and completes. The user ends up paying
 *     twice and staring at "volvé a intentar" when the answer is already
 *     stored in their row.
 *   - The automatic retry re-POSTs the same billable mutation. If the first
 *     attempt actually reached the server, the retry is a second provider
 *     charge, silently.
 *
 * This transport gives the legacy verification endpoints a dedicated 60s
 * budget, NEVER auto-retries, persists the record id + request id BEFORE the
 * call so a crashed/killed app can reconcile later, and telemetry-classifies
 * the abort so the outage is visible instead of invisible.
 *
 * Phase 0 (containment) of the verification platform plan. The final v2 API
 * replaces this with command ids and durable queues; until then the legacy
 * endpoints get a safe facade client-side.
 */
import { Platform } from 'react-native'
import * as Application from 'expo-application'
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import { env } from '../env'
import { fetchWithTimeout } from '../utils/fetch-with-timeout'
import { captureError } from '../utils/observability'
import { ServiceError } from '../errors/service-error'

/** Identity OCR and registry lookup run in separate requests. */
export const VERIFICATION_LEGACY_TIMEOUT_MS = 60_000
/** Sertracen alone can take 85s, before storage and database work. */
const LICENSE_VERIFICATION_TIMEOUT_MS = 120_000

type VerificationFnName =
  | 'verify-identity'
  | 'verify-license'
  | 'verify-selfie'
  | 'create-selfie-session'
  | 'refresh-selfie-session'

interface VerificationInvokeBody {
  identityDocumentId?: string
  driverLicenseId?: string
  [key: string]: unknown
}

interface VerificationInvokeOptions {
  body: VerificationInvokeBody
}

export type VerificationInvokeError = VerificationHttpError | VerificationNetworkError

/**
 * An error that carries the server's raw Response so the existing
 * `readEdgeFnError` helper can extract the coded `{ error, code }` body —
 * same shape FunctionsHttpError has.
 */
export class VerificationHttpError extends Error {
  readonly context: Response
  constructor(message: string, response: Response) {
    super(message)
    this.name = 'VerificationHttpError'
    this.context = response
  }
}

/**
 * The request never settled or the transport gave up on it. The server may
 * or may not have run the command — that is exactly why reconciliation
 * (reading the row back) is the only allowed follow-up, never a blind retry.
 */
export class VerificationNetworkError extends Error {
  readonly kind: 'timeout' | 'network' | 'aborted'
  constructor(kind: VerificationNetworkError['kind'], message: string) {
    super(message)
    this.name = 'VerificationNetworkError'
    this.kind = kind
  }
}

export function isVerificationAmbiguousError(err: unknown): boolean {
  return (
    err instanceof VerificationNetworkError ||
    (err instanceof ServiceError && err.code === 'VERIFICATION_INCOMPLETE')
  )
}

/**
 * Persisted right before a billable verification call, cleared when it
 * settles. Only ids, revisions and timestamps — never photos, names,
 * numbers or OCR. Enough for reconciliation after a kill.
 */
interface VerificationInflightMarker {
  fn: VerificationFnName
  recordId: string | null
  requestId: string
  startedAt: string
}

const MARKER_PREFIX = 'tago.verification.inflight.'

function inflightStore(): {
  get: (key: string) => string | undefined
  set: (key: string, value: string) => void
  delete: (key: string) => void
} {
  if (Platform.OS === 'web') {
    const web = typeof window !== 'undefined' ? window.localStorage : null
    return {
      get: (key) => web?.getItem(key) ?? undefined,
      set: (key, value) => web?.setItem(key, value),
      delete: (key) => web?.removeItem(key),
    }
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { MMKV } = require('react-native-mmkv') as typeof import('react-native-mmkv')
    const store = new MMKV({ id: 'tago-verification-inflight' })
    return {
      get: (key) => store.getString(key),
      set: (key, value) => store.set(key, value),
      delete: (key) => store.delete(key),
    }
  } catch {
    return {
      get: () => undefined,
      set: () => {},
      delete: () => {},
    }
  }
}

function clientBuildTag(): string {
  try {
    const version = Application.nativeApplicationVersion ?? ''
    const build = Application.nativeBuildVersion ?? ''
    return build ? `${version}(${build})` : version || 'unknown'
  } catch {
    return 'unknown'
  }
}

function clientRuntimeTag(): string {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const Updates = require('expo-updates') as typeof import('expo-updates')
    return Updates.isEmbeddedLaunch ? 'embedded' : (Updates.updateId ?? 'update-unknown')
  } catch {
    return 'embedded'
  }
}

class VerificationTransportImpl {
  /**
   * POST a verification command to a legacy edge function.
   *
   * - 60s budget, no automatic retry, ever.
   * - recordId + requestId persisted BEFORE the call.
   * - AbortError telemetry with stage/platform/build/update.
   */
  async invoke<T = unknown>(
    fn: VerificationFnName,
    options: VerificationInvokeOptions,
    opts: { recordId?: string | null } = {},
  ): Promise<{ data: T | null; error: VerificationInvokeError | null }> {
    const requestId = Crypto.randomUUID()
    const store = inflightStore()
    const marker: VerificationInflightMarker = {
      fn,
      recordId: opts.recordId ?? null,
      requestId,
      startedAt: new Date().toISOString(),
    }
    const markerKey = MARKER_PREFIX + fn
    const timeoutMs =
      fn === 'verify-license' ? LICENSE_VERIFICATION_TIMEOUT_MS : VERIFICATION_LEGACY_TIMEOUT_MS
    const controller = new AbortController()
    // Keep the deadline alive through response.json(), not only until headers.
    const deadline = setTimeout(() => controller.abort(), timeoutMs)
    let settled = false
    try {
      store.set(markerKey, JSON.stringify(marker))
    } catch {
      // Persistence is nice-to-have; the call must still run.
    }

    try {
      const session = await supabase.auth.getSession()
      const token = session.data.session?.access_token
      if (!token) {
        settled = true
        return {
          data: null,
          error: new VerificationNetworkError(
            'network',
            'Verification requires an authenticated session',
          ),
        }
      }

      const url = `${env.EXPO_PUBLIC_SUPABASE_URL}/functions/v1/${fn}`
      let response: Response
      try {
        response = await fetchWithTimeout(
          url,
          {
            method: 'POST',
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              'X-Trace-Id': requestId,
              'X-Client-Platform': Platform.OS,
              'X-Client-Build': clientBuildTag(),
              'X-Client-Runtime': clientRuntimeTag(),
            },
            // Match functions.invoke's options contract used by every caller.
            // Only the payload goes on the wire, never the { body } wrapper.
            body: JSON.stringify(options.body),
          },
          { timeoutMs, retry: false },
        )
      } catch (fetchError) {
        const isAbort =
          fetchError instanceof Error &&
          (fetchError.name === 'AbortError' || fetchError.message.includes('aborted'))
        const kind = isAbort ? 'timeout' : 'network'
        captureError(
          fetchError,
          {
            screen: 'verification',
            action: `${fn}:invoke`,
            stage: 'verify-invoke',
            platform: Platform.OS,
            build: clientBuildTag(),
            runtime: clientRuntimeTag(),
          },
          { recordId: opts.recordId ?? null, requestId },
        )
        return {
          data: null,
          error: new VerificationNetworkError(
            kind,
            isAbort
              ? `La verificación tardó más de lo esperado (${timeoutMs / 1000}s).`
              : 'No pudimos comunicarnos con el servidor de verificación.',
          ),
        }
      }

      if (!response.ok) {
        settled = response.status < 500 && response.status !== 408
        return {
          data: null,
          error: new VerificationHttpError(
            `Edge function ${fn} returned ${response.status}`,
            response,
          ),
        }
      }

      let data: T | null = null
      try {
        data = (await response.json()) as T
      } catch {
        return {
          data: null,
          error: new VerificationNetworkError('network', 'Verification response was unreadable'),
        }
      }
      if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return {
          data: null,
          error: new VerificationNetworkError('network', 'Verification response was invalid'),
        }
      }
      settled = true
      return { data, error: null }
    } finally {
      clearTimeout(deadline)
      try {
        // A timeout/5xx can arrive after a committed decision. Preserve its
        // marker, and never erase a newer concurrent request's marker.
        if (settled && store.get(markerKey) === JSON.stringify(marker)) store.delete(markerKey)
      } catch {
        // Best effort only.
      }
    }
  }

  /** The last in-flight marker for a verification fn, or null. */
  readInflightMarker(fn: VerificationFnName): VerificationInflightMarker | null {
    try {
      const raw = inflightStore().get(MARKER_PREFIX + fn)
      if (!raw) return null
      const parsed = JSON.parse(raw) as VerificationInflightMarker
      return typeof parsed.fn === 'string' ? parsed : null
    } catch {
      return null
    }
  }
}

export const verificationTransport = new VerificationTransportImpl()
